import React, { useState } from "react";
import {
  List,
  ListItem,
  ListItemText,
  Divider,
  Box,
  Typography,
  Button,
  Badge,
  useTheme,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Tooltip,
  Chip,
  alpha,
  CircularProgress,
  Tabs,
  Tab,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Autocomplete,
  TextField,
} from "@mui/material";
import { useConversations } from "../dataaccess/fetchLLMConversations";
import { updateLLMConversationSummary } from "../dataaccess/updateConversationSummary";
import { useLLM } from "llmasaservice-client";
import { useQueryClient, useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import ContactMailIcon from "@mui/icons-material/ContactMail";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ReactMarkdown from "react-markdown";
import AdsClickIcon from "@mui/icons-material/AdsClick";
import ThumbUpIcon from "@mui/icons-material/ThumbUp";
import ThumbDownIcon from "@mui/icons-material/ThumbDown";
import EmailIcon from "@mui/icons-material/Email";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import AssistantIcon from "@mui/icons-material/Assistant";
import SaveAltIcon from "@mui/icons-material/SaveAlt";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import BusinessIcon from "@mui/icons-material/Business";
import PersonIcon from "@mui/icons-material/Person";
import SupervisorAccountIcon from "@mui/icons-material/SupervisorAccount";
import MoodIcon from "@mui/icons-material/Mood";
import NeedIcon from "@mui/icons-material/TrendingUp"; // For "Need"
import LinkedInIcon from "@mui/icons-material/LinkedIn";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import { Prism as SyntaxHighlighter } from "react-syntax-highlighter";
import { materialDark } from "react-syntax-highlighter/dist/esm/styles/prism";
import rehypeRaw from "rehype-raw";
import remarkGfm from "remark-gfm";
import { updateLLMConversationIntelligence } from "../dataaccess/updateConversationIntelligence";
import { updateConversationCustomer } from "../dataaccess/updateConversationCustomer";
import { fetchLLMCustomers } from "../dataaccess/fetchLLMCustomers";
import MemoryAndKnowledgeGraph from "./MemoryAndKnowledgeGraph";
import { fetchAgentValidations } from "../dataaccess/fetchAgentValidations";
import { ValidationEngine } from "../utils/validationEngine";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorIcon from "@mui/icons-material/Error";
import WarningIcon from "@mui/icons-material/Warning";
import ValidationResults from "./ValidationResults";

const isEmail = (value: string): boolean => /^\S+@\S+\.\S+$/.test(value);

const ConversationsList: React.FC<{
  projectId: string;
  nameFilter: string | undefined;
}> = ({ projectId, nameFilter }) => {
  const {
    data,
    error,
    isLoading,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useConversations(projectId, 25);

  //invalidate the react query cache
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const theme = useTheme();

  const [isGeneratingIntelligence, setIsGeneratingIntelligence] =
    useState(false);
  const [tabValue, setTabValue] = useState(0);
  const [customerDialogOpen, setCustomerDialogOpen] = useState(false);
  const [selectedConversationId, setSelectedConversationId] = useState<
    string | null
  >(null);
  const [customerSearchText, setCustomerSearchText] = useState("");
  const [validationResults, setValidationResults] = useState<{
    [conversationId: string]: any;
  }>({});
  const [isValidating, setIsValidating] = useState<{
    [conversationId: string]: boolean;
  }>({});
  const [validationResultsViewed, setValidationResultsViewed] = useState<{
    [conversationId: string]: boolean;
  }>({});
  const [activeConversationId, setActiveConversationId] = useState<
    string | null
  >(null);

  // Helper function to mark validation results as viewed
  const markValidationResultsAsViewed = (conversationId: string) => {
    setValidationResultsViewed((prev) => ({ ...prev, [conversationId]: true }));
  };

  // Fetch customers for the autocomplete
  const { data: customersData } = useQuery({
    queryKey: ["customers", projectId],
    queryFn: () => fetchLLMCustomers(projectId),
    enabled: !!projectId,
  });

  let url = "https://chat.llmasaservice.io/";
  // if the url is localhost or dev.llmasaservice.io, we are in development mode and we should use the dev endpoint
  if (
    window.location.hostname === "localhost" ||
    window.location.hostname === "dev.llmasaservice.io"
  ) {
    url = "https://chat.llmasaservice.io/dev";
  }

  const { send, idle } = useLLM({ project_id: projectId ?? "", url: url });

  // Customer selection handlers
  const openCustomerDialog = (conversationId: string) => {
    setSelectedConversationId(conversationId);
    setCustomerDialogOpen(true);
    setCustomerSearchText("");
  };

  const closeCustomerDialog = () => {
    setCustomerDialogOpen(false);
    setSelectedConversationId(null);
    setCustomerSearchText("");
  };

  const assignCustomerToConversation = async (customerId: string | null) => {
    if (!selectedConversationId) return;

    try {
      await updateConversationCustomer(selectedConversationId, customerId);
      // Invalidate the conversations query to refresh the data
      queryClient.invalidateQueries({ queryKey: ["conversations"] });
      closeCustomerDialog();
    } catch (error) {
      console.error("Error assigning customer to conversation:", error);
      // You might want to show a toast notification here
    }
  };

  // Validation handler
  const validateConversation = async (conversation: any) => {
    if (!conversation.agentId || !conversation.calls?.items?.length) {
      return;
    }

    const conversationId = conversation.id;
    setIsValidating((prev) => ({ ...prev, [conversationId]: true }));

    try {
      // Get active validations for this conversation's agent
      const validationsResponse = await fetchAgentValidations(
        conversation.agentId
      );

      const validations =
        validationsResponse?.data?.validationsByAgentId?.items?.filter(
          (v: any) => v.status === "ACTIVE"
        ) || [];

      if (validations.length === 0) {
        setValidationResults((prev) => ({
          ...prev,
          [conversationId]: null, // Will show "No Results Available" message
        }));
        // Switch to validation results tab to show the "no validations" message
        setTabValue(2);
        return;
      }

      // Get the most recent call (conversation data)
      const mostRecentCall = conversation.calls.items[0];

      if (!mostRecentCall.messages) {
        setValidationResults((prev) => ({
          ...prev,
          [conversationId]: null, // Will show "No Results Available" message
        }));
        // Switch to validation results tab to show the error
        setTabValue(2);
        return;
      }

      // Parse messages
      const messages = JSON.parse(mostRecentCall.messages);

      // Convert to OpenAI-like message format
      const openAIMessages = messages.map((msg: any) => ({
        role: msg.role,
        content: msg.content,
        tool_calls: msg.tool_calls || undefined,
      }));

      // Add the prompt and response from this call as additional messages
      openAIMessages.push({
        role: "user",
        content: mostRecentCall.prompt,
      });

      openAIMessages.push({
        role: "assistant",
        content: mostRecentCall.response,
      });

      // Transform validations to match ValidationRule interface
      const validationRules = validations
        .filter((v: any) => v != null)
        .map((v: any) => ({
          id: v.id,
          name: v.name || "",
          operator: v.operator,
          expectedValue: v.expectedValue || "",
          customPromptTemplate: v.customPromptTemplate || "",
        }));

      // Create validation engine and execute validations
      const llmSendWrapper = async (
        prompt: string,
        messages: any[],
        tools: any[],
        isUserMessage: boolean
      ): Promise<string | null> => {
        try {
          const result = await send(prompt, messages, [], false, false);
          return typeof result === "string" ? result : null;
        } catch (error) {
          console.error("LLM send wrapper error:", error);
          return null;
        }
      };

      const results = await ValidationEngine.executeValidations(
        openAIMessages,
        validationRules,
        conversationId,
        conversation.agentId,
        llmSendWrapper // Pass the wrapped LLM send function for CUSTOM_LLM_JUDGE validations
      );

      // Store the complete results object for the ValidationResults component
      setValidationResults((prev) => ({
        ...prev,
        [conversationId]: results,
      }));

      // Mark results as not viewed since they're new
      setValidationResultsViewed((prev) => ({
        ...prev,
        [conversationId]: false,
      }));

      // Auto-switch to validation results tab to show the results
      setTabValue(2);
      setActiveConversationId(conversationId);
    } catch (error) {
      console.error("❌ Error validating conversation:", error);
      setValidationResults((prev) => ({
        ...prev,
        [conversationId]: {
          error: `Validation failed: ${
            error instanceof Error ? error.message : "Unknown error"
          }`,
          validations: [],
        },
      }));
      // Mark results as not viewed even for errors
      setValidationResultsViewed((prev) => ({
        ...prev,
        [conversationId]: false,
      }));
      // Switch to validation results tab to show the error
      setTabValue(2);
      setActiveConversationId(conversationId);
    } finally {
      setIsValidating((prev) => ({ ...prev, [conversationId]: false }));
    }
  };

  // Prepare customer list for autocomplete
  const customers =
    customersData?.data?.customersByProjectIdAndLastCallMadeAt?.items || [];
  const customerOptions = customers.map((customer: any) => {
    const isEmailName = isEmail(customer.name || "");
    const displayName = isEmailName
      ? customer.name
      : customer.name || "Unknown";
    const displayEmail = isEmailName
      ? customer.name
      : customer.emailAddress || "No email";

    return {
      id: customer.id,
      name: displayName,
      email: displayEmail,
      label: isEmailName ? customer.name : `${displayName} (${displayEmail})`,
    };
  });

  if (isLoading) return <div>Loading...</div>;
  if (error) return <div>Error: {error.message}</div>;

  // Helper to group conversations by created date
  const groupConversations = (conversations: any[]) => {
    const groups: { [key: string]: any[] } = {
      Today: [],
      "This Week": [],
      Older: [],
    };

    const now = new Date();
    conversations
      .filter((conv) => {
        return (
          conv.calls?.items?.length > 0 &&
          (conv.customer?.name ?? "").includes(nameFilter ?? "")
        );
      })
      .forEach((conv) => {
        const createdDate = new Date(conv.createdAt);
        const diffTime = now.getTime() - createdDate.getTime();
        const diffDays = diffTime / (1000 * 3600 * 24);

        if (
          now.getDate() === createdDate.getDate() &&
          now.getMonth() === createdDate.getMonth() &&
          now.getFullYear() === createdDate.getFullYear()
        ) {
          groups["Today"].push(conv);
        } else if (diffDays < 7) {
          groups["This Week"].push(conv);
        } else {
          groups["Older"].push(conv);
        }
      });
    return groups;
  };

  const allConversations =
    data?.pages.flatMap((page) => page.conversations) || [];
  const grouped = groupConversations(allConversations);

  const summarizeConversation = async (conversation: any) => {
    console.log("Summarizing conversation", conversation.id);

    if (!conversation.calls?.items || conversation.calls.items.length === 0) {
      return;
    }

    try {
      // find the most recent call
      const mostRecentCall = conversation.calls.items[0];
      const messages = JSON.parse(mostRecentCall.messages);

      // add the prompt as a user message at the end
      messages.push({
        role: "user",
        content: mostRecentCall.prompt,
      });

      // add the last response
      messages.push({
        role: "assistant",
        content: mostRecentCall.response,
      });

      const llmSummary = await send(
        "Write a 75 word or less summary of what this conversation is about by summarizing what the customer is asking for in the messages.",
        messages,
        [],
        false
      );

      const llmtitle = await send(
        "Respond with ONLY a three word title for this conversation, no formatting or markup.",
        messages,
        [],
        false
      );

      if (llmSummary && llmtitle) {
        // update the conversation with the summary
        await updateLLMConversationSummary(
          conversation.id,
          "active",
          removeLeadingAndTrailingQuotes(llmSummary as string),
          removeLeadingAndTrailingQuotes(llmtitle as string)
        );
        queryClient.invalidateQueries({
          queryKey: ["conversations", projectId],
        });
      }
    } catch (err) {
      console.error("Failed to parse messages", err);
    }
  };

  const removeLeadingAndTrailingQuotes = (str: string) => {
    return str
      .replace(/^"(.*)"$/, "$1")
      .replace("(cached)", "")
      .replace("(PII redacted)", "")
      .trim();
  };

  /* removed

     - Identify the overall sentiment of each message (e.g., “positive,” “negative,” or “neutral”).
   - Include a brief rationale.
      "sentimentAnalysis": [
      {
        "messageId": "<unique identifier>",
        "textExcerpt": "<message text or a short excerpt>",
        "sentiment": "<positive|negative|neutral>",
        "rationale": "<brief explanation>"
      }
      // ... repeat for additional messages
    ],

    */

  const analyzeConversation = async (conversation: any) => {
    setIsGeneratingIntelligence(true);
    try {
      const newAnalyzeInstructions = `
SYSTEM INSTRUCTION

You are a Conversation Analysis Assistant. For every conversation, do the following:

1. **Company Details from Email Domains**
   - If an email address is present, parse out the domain (e.g., “acme.com”).
   - Provide publicly available company information from that domain if known (e.g., “Acme Inc.” and size). 

2. **Individual Detail from Email**
   - If an email address is present
   - Provide publicly available personal information from that email, e.g. their linked in profile

3. **Sentiment Analysis**
   - Identify the overall sentiment of the conversation

4. **BANT Analysis**
   - Check for Budget, Authority, Need, and Timeline factors.
   - For each factor, provide one of the following:
     - “Yes,” “No,” or “Not Specified” (if no information is available).
     - A single sentence summary of the specific information available
   - Optionally, include any helpful clarifications or relevant details.

5. **Output Format**
   - Provide your final analysis **strictly** as a single JSON object matching the schema below.
   - No additional text, code blocks, explanations, or formatting outside the JSON.

**Required Schema** (with all listed properties):

\`\`\`json
{
  "$schema": "http://json-schema.org/draft-07/schema#",
  "$id": "https://example.com/analysis.schema.json",
  "title": "Analysis Schema",
  "type": "object",
  "properties": {
    "analysis": {
      "type": "object",
      "properties": {
        "companyDetails": {
          "type": "array",
          "items": {
            "type": "object",
            "properties": {
              "emailAddress": {
                "type": "string",
                "format": "email"
              },
              "domain": {
                "type": "string"
              },
              "companyName": {
                "type": "string"
              },
              "companySize": {
                "type": "string"
              },
              "additionalInfo": {
                "type": "string"
              }
            },
            "required": [
              "emailAddress",
              "domain"
            ],
            "additionalProperties": false
          }
        },
        "personalDetails": {
          "type": "array",
          "items": {
            "type": "object",
            "properties": {
              "emailAddress": {
                "type": "string",
                "format": "email"
              },
              "name": {
                "type": "string"
              },
              "linkedIn": {
                "type": "string"
              },
              "additionalInfo": {
                "type": "string"
              }
            },
            "required": [
              "emailAddress"
            ],
            "additionalProperties": false
          }
        },
        "sentimentOverall": {
          "type": "string",
          "enum": [
            "positive",
            "negative",
            "neutral"
          ]
        },
        "bantAnalysis": {
          "type": "object",
          "properties": {
            "budget": {
              "type": "string",
              "enum": [
                "Yes",
                "No",
                "Not Specified"
              ]
            },
            "budgetDetail": {
              "type": "string"
            },
            "authority": {
              "type": "string",
              "enum": [
                "Yes",
                "No",
                "Not Specified"
              ]
            },
            "authorityDetail": {
              "type": "string"
            },
            "need": {
              "type": "string",
              "enum": [
                "Yes",
                "No",
                "Not Specified"
              ]
            },
            "needDetail": {
              "type": "string"
            },
            "timeline": {
              "type": "string",
              "enum": [
                "Yes",
                "No",
                "Not Specified"
              ]
            },
            "timelineDetail": {
              "type": "string"
            },
            "details": {
              "type": "string"
            }
          },
          "required": [
            "budget",
            "budgetDetail",
            "authority",
            "authorityDetail",
            "need",
            "needDetail",
            "timeline",
            "timelineDetail",
            "details"
          ],
          "additionalProperties": false
        }
      },
      "required": [
        "companyDetails",
        "personalDetails",
        "sentimentOverall",
        "bantAnalysis"
      ],
      "additionalProperties": false
    }
  },
  "required": [
    "analysis"
  ],
  "additionalProperties": false
}
\`\`\`

**EXAMPLE:**
\`\`\`json
{
  "analysis": {
    "companyDetails": [
      {
        "emailAddress": "<user@domain.com>",
        "domain": "<domain.com>",
        "companyName": "<if known>",
        "companySize": "<if known>",
        "additionalInfo": "<any relevant public info>",
      },
      // ... repeat if multiple email domains appear
    ],
    "personalDetails": [
      {
        "emailAddress": "<user@domain.com>",
        "name": ",users name guessed or provided>",
        "linkedIn": "<linkedIn profile>",
        "additionalInfo": "<any relevant public info>"
      },
      // ... repeat if multiple email addresses appear
    ],
    "sentimentOverall": "<positive|negative|neutral>",
    "bantAnalysis": {
      "budget": "<Yes|No|Not Specified>",
      "budgetDetail": "<budget details>",
      "authority": "<Yes|No|Not Specified>",
      "authorityDetail": "<authority details>",
      "need": "<Yes|No|Not Specified>",
      "needDetail": "<need details>",
      "timeline": "<Yes|No|Not Specified>",
      "timelineDetail": "<timeline details>",
      "details": "<any clarifications or relevant info>",
    }
  }
}\`\`\`

**Your final response must be valid JSON conforming to the schema above. Output nothing else.**

END OF SYSTEM INSTRUCTION
`;

      let prompt = `Using the SYSTEM INSTRUCTION specified, analyze this conversation and respond in the JSON structure specified.
    
    The users email address if not specified in the conversation is ${
      conversation?.emailAddress ?? "not known"
    }. 
    
    `;

      // add the conversation messages to the prompt
      const convo =
        conversation.calls?.items?.map((call: any, index: number) => [
          {
            id: "prompt" + index,
            role: "user",
            content: call?.prompt,
          },
          {
            id: "response" + index,
            role: "assistant",
            content: call.response,
          },
        ]) || [];

      prompt += JSON.stringify(convo, null, 2);

      try {
        const analysis = await send(
          prompt,
          [
            {
              role: "system",
              content: newAnalyzeInstructions,
            },
          ],
          [],
          false,
          false
        );

        console.log("Analysis response:", analysis);

        let parsedAnalysis: any = null;
        try {
          // Attempt to parse the JSON response with more robust cleanup
          let cleanedJson = (analysis as string)
            .replace(/```json/g, "") // Remove json code block markers
            .replace(/```/g, "") // Remove generic code block markers
            .replace(/\(PII redacted\)/g, "") // Remove PII notice
            .replace(/\(cached\)/g, ""); // Remove cached notice

          // Fix common JSON structure issues
          // This regex looks for a property with incomplete value followed by another property
          cleanedJson = cleanedJson.replace(
            /"rationale": "([^"]*)",\s*"(\w+)":/g,
            '"rationale": "$1"},{"$2":'
          );

          // Make sure we have proper structure for BANT analysis
          if (
            !cleanedJson.includes('"bantAnalysis"') &&
            cleanedJson.includes('"budget"')
          ) {
            cleanedJson = cleanedJson.replace(
              /"budget":/g,
              '"bantAnalysis": {"budget":'
            );
            // Add closing brace for bantAnalysis if needed
            if (!cleanedJson.match(/}\s*}\s*}\s*$/)) {
              cleanedJson = cleanedJson.replace(/}\s*}\s*$/, "}}}");
            }
          }

          // Sometimes the sentiment overall is missing - add a default if that's the case
          if (!cleanedJson.includes('"sentimentOverall"')) {
            cleanedJson = cleanedJson.replace(
              /"sentimentAnalysis": \[\s*{/g,
              '"sentimentAnalysis": [{'
            );
            cleanedJson = cleanedJson.replace(
              /}\s*\]\s*,\s*"bantAnalysis"/g,
              '}],"sentimentOverall": "neutral","bantAnalysis"'
            );
          }

          console.log("Cleaned JSON:", cleanedJson);

          parsedAnalysis = JSON.parse(cleanedJson);
        } catch (e) {
          // If it's still not valid JSON, try to extract just the BANT analysis
          console.error("Failed to parse Cleaned JSON", e);

          try {
            // Try to extract just the BANT section as a fallback
            const bantMatch = (analysis as string).match(
              /"budget"[^}]+},?\s*"authority"[^}]+},?\s*"need"[^}]+},?\s*"timeline"[^}]+}/
            );
            if (bantMatch) {
              parsedAnalysis = {
                analysis: {
                  bantAnalysis: JSON.parse(`{${bantMatch[0]}}`),
                },
              };
            } else {
              // If we can't even extract BANT, store as text
              parsedAnalysis = { rawText: analysis };
            }
          } catch (bantError) {
            parsedAnalysis = { rawText: analysis };
          }
        }

        await updateLLMConversationIntelligence(
          conversation.id,
          null,
          JSON.stringify(parsedAnalysis, null, 2)
        );
        queryClient.invalidateQueries({
          queryKey: ["conversations", projectId],
        });
      } catch (error) {
        console.error("Analysis failed:", error);
      }
    } finally {
      setIsGeneratingIntelligence(false);
    }
  };

  const CodeBlock = ({ node, className, children, style, ...props }: any) => {
    const match = /language-(\w+)/.exec(className || "");

    return match ? (
      <>
        <div
          style={{
            border: 0,
            padding: 0,
            height: "16px",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <span>{match ? match[1] : "Unknown"}</span>
          <button
            onClick={() => {
              //todo
            }}
            className="copy-button"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 320 320"
              fill="currentColor"
              style={{
                width: "16px",
                height: "16px",
                margin: 0,
                border: 0,
              }}
            >
              <path
                d="M35,270h45v45c0,8.284,6.716,15,15,15h200c8.284,0,15-6.716,15-15V75c0-8.284-6.716-15-15-15h-45V15
		c0-8.284-6.716-15-15-15H35c-8.284,0-15,6.716-15,15v240C20,263.284,26.716,270,35,270z M280,300H110V90h170V300z M50,30h170v30H95
		c-8.284,0-15,6.716-15,15v165H50V30z"
              />
              <path d="M155,120c-8.284,0-15,6.716-15,15s6.716,15,15,15h80c8.284,0,15-6.716,15-15s-6.716-15-15-15H155z" />
              <path d="M235,180h-80c-8.284,0-15,6.716-15,15s6.716,15,15,15h80c8.284,0,15-6.716,15-15S243.284,180,235,180z" />
              <path
                d="M235,240h-80c-8.284,0-15,6.716-15,15c0,8.284,6.716,15,15,15h80c8.284,0,15-6.716,15-15C250,246.716,243.284,240,235,240z
		"
              />
            </svg>
          </button>
        </div>
        <SyntaxHighlighter
          style={materialDark}
          PreTag="div"
          language={match[1]}
          {...props}
        >
          {String(children).replace(/\n$/, "")}
        </SyntaxHighlighter>
      </>
    ) : (
      <code className={className ? className : ""} {...props}>
        {children}
      </code>
    );
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
    // If switching to validation results tab and there's an active conversation, mark results as viewed
    if (
      newValue === 2 &&
      activeConversationId &&
      validationResults[activeConversationId]
    ) {
      markValidationResultsAsViewed(activeConversationId);
    }
  };

  return (
    <Box p={2}>
      <Typography variant="h5">Conversations</Typography>

      {Object.entries(grouped).map(([groupName, conversations]) => (
        <Box key={groupName} mb={3}>
          {conversations.length > 0 && (
            <>
              <Typography
                variant="subtitle1"
                sx={{ fontWeight: "bold", mb: 1 }}
              >
                {groupName}
              </Typography>
              {!idle && (
                <Typography variant="caption">generating summary...</Typography>
              )}
              <List>
                {conversations.map((conversation: any) => (
                  <React.Fragment key={conversation.id}>
                    <Accordion
                      onChange={(event, isExpanded) => {
                        if (isExpanded) {
                          setActiveConversationId(conversation.id);
                        }
                      }}
                    >
                      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <ListItem alignItems="flex-start">
                          <Box
                            mr={2}
                            sx={{
                              minWidth: 112,
                              display: "flex",
                              flexDirection: "column",
                              alignItems: "flex-start",
                              gap: 0.75,
                            }}
                          >
                            <Chip
                              size="small"
                              color="primary"
                              variant="outlined"
                              label={`${conversation.calls?.items?.length || 0} ${
                                (conversation.calls?.items?.length || 0) === 1
                                  ? "call"
                                  : "calls"
                              }`}
                            />

                            <Box
                              sx={{
                                display: "flex",
                                flexWrap: "wrap",
                                gap: 0.5,
                              }}
                            >
                              {(conversation?.ctaClicked ?? 0) > 0 && (
                                <Tooltip title="Call to Action Clicked">
                                  <Chip
                                    size="small"
                                    icon={<ContactMailIcon />}
                                    label={conversation.ctaClicked}
                                    color="warning"
                                    variant="outlined"
                                  />
                                </Tooltip>
                              )}
                              {(conversation?.thumbsdownClicked ?? 0) > 0 && (
                                <Tooltip title="Thumbs Down Clicked">
                                  <Chip
                                    size="small"
                                    icon={<ThumbDownIcon />}
                                    label={conversation.thumbsdownClicked}
                                    color="warning"
                                    variant="outlined"
                                  />
                                </Tooltip>
                              )}
                              {(conversation?.actionClicked ?? 0) > 0 && (
                                <Tooltip title="Response Action Clicked">
                                  <Chip
                                    size="small"
                                    icon={<AdsClickIcon />}
                                    label={conversation.actionClicked}
                                    color="success"
                                    variant="outlined"
                                  />
                                </Tooltip>
                              )}
                              {(conversation?.thumbsupClicked ?? 0) > 0 && (
                                <Tooltip title="Thumbs Up Clicked">
                                  <Chip
                                    size="small"
                                    icon={<ThumbUpIcon />}
                                    label={conversation.thumbsupClicked}
                                    color="success"
                                    variant="outlined"
                                  />
                                </Tooltip>
                              )}
                              {(conversation?.copyClicked ?? 0) > 0 && (
                                <Tooltip title="Copy Clicked">
                                  <Chip
                                    size="small"
                                    icon={<ContentCopyIcon />}
                                    label={conversation.copyClicked}
                                    color="success"
                                    variant="outlined"
                                  />
                                </Tooltip>
                              )}
                              {(conversation?.emailClicked ?? 0) > 0 && (
                                <Tooltip title="Email Clicked">
                                  <Chip
                                    size="small"
                                    icon={<EmailIcon />}
                                    label={conversation.emailClicked}
                                    color="success"
                                    variant="outlined"
                                  />
                                </Tooltip>
                              )}
                              {(conversation?.saveClicked ?? 0) > 0 && (
                                <Tooltip title="Save Clicked">
                                  <Chip
                                    size="small"
                                    icon={<SaveAltIcon />}
                                    label={conversation.saveClicked}
                                    color="success"
                                    variant="outlined"
                                  />
                                </Tooltip>
                              )}
                              {(conversation?.suggestionClicked ?? 0) > 0 && (
                                <Tooltip title="Suggestion Clicked">
                                  <Chip
                                    size="small"
                                    icon={<AssistantIcon />}
                                    label={conversation.suggestionClicked}
                                    color="success"
                                    variant="outlined"
                                  />
                                </Tooltip>
                              )}
                            </Box>
                          </Box>

                          <ListItemText
                            primary={
                              <>
                                {conversation.customer?.name &&
                                isEmail(conversation.customer.name) ? (
                                  <Box
                                    sx={{
                                      display: "inline-flex",
                                      alignItems: "center",
                                      gap: 1,
                                    }}
                                  >
                                  {conversation.agent?.name ?? "no agent"}&nbsp;by&nbsp; 
                                    {conversation.customer.name}
                                    <Button
                                      size="small"
                                      variant="text"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        openCustomerDialog(conversation.id);
                                      }}
                                      sx={{
                                        minWidth: "auto",
                                        px: 0.5,
                                        fontSize: "0.7rem",
                                      }}
                                    >
                                      Change
                                    </Button>
                                  </Box>
                                ) : (conversation.customer?.name ?? "") !==
                                  "" ? (
                                  <Box
                                    sx={{
                                      display: "inline-flex",
                                      alignItems: "center",
                                      gap: 1,
                                    }}
                                  >
                                    <span>{conversation.customer.name}</span>
                                    <Button
                                      size="small"
                                      variant="text"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        openCustomerDialog(conversation.id);
                                      }}
                                      sx={{
                                        minWidth: "auto",
                                        px: 0.5,
                                        fontSize: "0.7rem",
                                      }}
                                    >
                                      Change
                                    </Button>
                                  </Box>
                                ) : (
                                  <Box
                                    sx={{
                                      display: "inline-flex",
                                      alignItems: "center",
                                      gap: 1,
                                    }}
                                  >
                                    <span>Anonymous</span>
                                    <Button
                                      size="small"
                                      variant="outlined"
                                      startIcon={<PersonAddIcon />}
                                      onClick={(e) => {
                                        e.stopPropagation(); // Prevent accordion expansion
                                        openCustomerDialog(conversation.id);
                                      }}
                                      sx={{ minWidth: "auto", px: 1 }}
                                    >
                                      Choose Customer
                                    </Button>
                                  </Box>
                                )}
                                &nbsp;{" "}
                                {conversation?.calls?.items &&
                                  conversation?.calls?.items.length > 0 && (
                                    <Tooltip title="Country">
                                      <Chip
                                        size="small"
                                        variant="outlined"
                                        label={
                                          conversation?.calls?.items[0]?.country
                                        }
                                      />
                                    </Tooltip>
                                  )}
                                {conversation?.timezone &&
                                  conversation?.timezone !== "unknown" && (
                                    <Tooltip title="User's timezone">
                                      <Chip
                                        size="small"
                                        variant="outlined"
                                        label={conversation?.timezone
                                          .replace("_", " ")
                                          .toLowerCase()}
                                      />
                                    </Tooltip>
                                  )}
                                {conversation?.language &&
                                  conversation?.language !== "unknown" && (
                                    <Tooltip title="User's language">
                                      <Chip
                                        size="small"
                                        variant="outlined"
                                        label={conversation?.language
                                          .replace("_", " ")
                                          .toLowerCase()}
                                      />
                                    </Tooltip>
                                  )}{" "}
                                - {conversation.title} (
                                {new Date(
                                  conversation.createdAt
                                ).toLocaleDateString()}
                                )
                              </>
                            }
                            secondary={
                              <>
                                {conversation.summary}
                                <br />
                                {/*
                                <Button
                                  size="small"
                                  variant="text"
                                  onClick={async () => {
                                    await updateLLMConversationSummary(
                                      conversation.id,
                                      "archived",
                                      conversation.summary || "",
                                      conversation.title || ""
                                    );
                                    queryClient.invalidateQueries({
                                      queryKey: ["conversations", projectId],
                                    });
                                  }}
                                >
                                  Archive
                                </Button>
                                &nbsp;|&nbsp; */}
                                <Button
                                  size="small"
                                  variant="text"
                                  disabled={!conversation.customer?.id}
                                  onClick={() =>
                                    navigate(
                                      `/customers?customerId=${conversation.customer?.id}`
                                    )
                                  }
                                >
                                  View Customer
                                </Button>
                                &nbsp;|&nbsp;
                                <Button
                                  size="small"
                                  variant="text"
                                  onClick={async () => {
                                    await summarizeConversation(conversation);
                                  }}
                                  disabled={!idle}
                                >
                                  {!conversation.summary ||
                                  conversation.summary === ""
                                    ? "Summarize"
                                    : "Refresh summary"}
                                </Button>
                                &nbsp;|&nbsp;
                                <Tooltip
                                  title={
                                    validationResults[conversation.id]
                                      ? "View validation results in the Validation Results tab"
                                      : "Run validation (results will appear in Validation Results tab)"
                                  }
                                >
                                  <Button
                                    size="small"
                                    variant="text"
                                    onClick={() =>
                                      validateConversation(conversation)
                                    }
                                    disabled={
                                      isValidating[conversation.id] ||
                                      !conversation.agentId
                                    }
                                    startIcon={
                                      isValidating[conversation.id] ? (
                                        <CircularProgress size={12} />
                                      ) : validationResults[conversation.id] ? (
                                        validationResults[conversation.id]
                                          .passedRules ===
                                        validationResults[conversation.id]
                                          .totalRules ? (
                                          <CheckCircleIcon
                                            fontSize="small"
                                            color="success"
                                          />
                                        ) : validationResults[conversation.id]
                                            .passedRules === 0 ? (
                                          <ErrorIcon
                                            fontSize="small"
                                            color="error"
                                          />
                                        ) : (
                                          <WarningIcon
                                            fontSize="small"
                                            color="warning"
                                          />
                                        )
                                      ) : null
                                    }
                                  >
                                    {isValidating[conversation.id]
                                      ? "Validating..."
                                      : "Validate"}
                                  </Button>
                                </Tooltip>
                              </>
                            }
                          ></ListItemText>
                        </ListItem>
                      </AccordionSummary>
                      <AccordionDetails>
                        <Box
                          sx={{ display: "flex", flexDirection: "row", gap: 2 }}
                        >
                          {(() => {
                            let parsedIntelligence = null;
                            try {
                              parsedIntelligence = conversation.intelligence
                                ? JSON.parse(conversation.intelligence)
                                : null;
                            } catch (e) {
                              console.error(
                                "Failed to parse conversation intelligence:",
                                e
                              );
                            }

                            return (
                              <>
                                {/* Left side: Conversation (2/3 width) */}
                                <Box
                                  sx={{
                                    display: "flex",
                                    flexDirection: "column",
                                    gap: 1,
                                    width: "50%",
                                  }}
                                >
                                  <Typography variant="h6" gutterBottom>
                                    Conversation
                                  </Typography>
                                  {conversation.calls?.items?.map(
                                    (call: any, index: number) => (
                                      <Box
                                        key={index}
                                        sx={{
                                          display: "flex",
                                          flexDirection: "column",
                                          gap: 1,
                                        }}
                                      >
                                        <Box
                                          sx={{
                                            bgcolor:
                                              theme.palette.background.paper,
                                            p: 1,
                                            borderRadius: 2,
                                            maxWidth: "90%",
                                          }}
                                        >
                                          <Typography
                                            component="div"
                                            variant="body2"
                                          >
                                            <ReactMarkdown>
                                              {call?.prompt?.includes(
                                                "CONTEXT:"
                                              )
                                                ? call?.prompt
                                                    ?.split("CONTEXT:")[0]
                                                    ?.split("PROMPT:")[1] || ""
                                                : call?.prompt}
                                            </ReactMarkdown>
                                          </Typography>
                                        </Box>
                                        <Box
                                          sx={{
                                            bgcolor:
                                              theme.palette.background.paper,
                                            p: 1,
                                            borderRadius: 2,
                                            maxWidth: "90%",
                                            alignSelf: "flex-end",
                                          }}
                                        >
                                          <Typography
                                            component="div"
                                            variant="body2"
                                            sx={{ mb: 1, overflowY: "auto" }}
                                          >
                                            <ReactMarkdown
                                              remarkPlugins={[remarkGfm]}
                                              rehypePlugins={[rehypeRaw]}
                                              components={{
                                                code: CodeBlock,
                                              }}
                                            >
                                              {call.response}
                                            </ReactMarkdown>
                                          </Typography>
                                        </Box>
                                      </Box>
                                    )
                                  )}
                                </Box>

                                {/* Right side: Tabbed Intelligence Section (1/2 width) */}
                                <Box
                                  sx={{
                                    width: "50%",
                                    bgcolor: theme.palette.background.default,
                                    p: 2,
                                    borderRadius: 2,
                                    border: `1px solid ${theme.palette.divider}`,
                                  }}
                                >
                                  <Tabs
                                    value={tabValue}
                                    onChange={handleTabChange}
                                    variant="fullWidth"
                                    sx={{ mb: 2 }}
                                  >
                                    <Tab label="Conversation Intelligence" />
                                    <Tab label="Customer Memories" />
                                    <Tab
                                      label={
                                        <Box
                                          sx={{
                                            display: "flex",
                                            alignItems: "center",
                                            gap: 1,
                                          }}
                                        >
                                          Validation Results
                                          {validationResults[conversation.id] &&
                                            !validationResultsViewed[
                                              conversation.id
                                            ] && (
                                              <Badge
                                                badgeContent={
                                                  validationResults[
                                                    conversation.id
                                                  ].passedRules ===
                                                  validationResults[
                                                    conversation.id
                                                  ].totalRules
                                                    ? "✓"
                                                    : validationResults[
                                                        conversation.id
                                                      ].passedRules === 0
                                                    ? "✗"
                                                    : "!"
                                                }
                                                color={
                                                  validationResults[
                                                    conversation.id
                                                  ].passedRules ===
                                                  validationResults[
                                                    conversation.id
                                                  ].totalRules
                                                    ? "success"
                                                    : validationResults[
                                                        conversation.id
                                                      ].passedRules === 0
                                                    ? "error"
                                                    : "warning"
                                                }
                                                sx={{
                                                  "& .MuiBadge-badge": {
                                                    fontSize: 10,
                                                    minWidth: 16,
                                                    height: 16,
                                                  },
                                                }}
                                              />
                                            )}
                                        </Box>
                                      }
                                    />
                                  </Tabs>

                                  {/* Intelligence Tab */}
                                  {tabValue === 0 && (
                                    <Box>
                                      <Typography variant="h6" gutterBottom>
                                        Conversation Intelligence
                                      </Typography>

                                      <Button
                                        variant="contained"
                                        size="small"
                                        onClick={() =>
                                          analyzeConversation(conversation)
                                        }
                                        disabled={!idle}
                                        sx={{ mb: 2 }}
                                      >
                                        {!parsedIntelligence?.analysis
                                          ? "Analyze now"
                                          : "Regenerate"}
                                      </Button>

                                      {isGeneratingIntelligence && (
                                        <Box
                                          sx={{
                                            display: "flex",
                                            alignItems: "center",
                                            gap: 1,
                                          }}
                                        >
                                          <CircularProgress size={20} />
                                          <Typography variant="body2">
                                            Generating insights...
                                          </Typography>
                                        </Box>
                                      )}

                                      {!parsedIntelligence?.analysis &&
                                      !isGeneratingIntelligence ? (
                                        <Typography
                                          variant="body2"
                                          color="text.secondary"
                                          sx={{ fontStyle: "italic" }}
                                        >
                                          Click Analyze to generate insights
                                        </Typography>
                                      ) : (
                                        <>
                                          {/* Company Info Card */}
                                          {parsedIntelligence?.analysis
                                            ?.companyDetails?.[0] && (
                                            <Box
                                              sx={{
                                                p: 2,
                                                bgcolor:
                                                  theme.palette.background
                                                    .paper,
                                                borderRadius: 1,
                                                boxShadow: 1,
                                              }}
                                            >
                                              <Typography
                                                variant="subtitle2"
                                                sx={{
                                                  fontWeight: "bold",
                                                  display: "flex",
                                                  alignItems: "center",
                                                  gap: 1,
                                                  mb: 1,
                                                }}
                                              >
                                                <BusinessIcon fontSize="small" />{" "}
                                                Company
                                              </Typography>
                                              <Box
                                                sx={{
                                                  display: "flex",
                                                  flexDirection: "column",
                                                  gap: 0.5,
                                                }}
                                              >
                                                <Typography variant="body2">
                                                  <strong>Name:</strong>{" "}
                                                  {parsedIntelligence?.analysis
                                                    .companyDetails[0]
                                                    .companyName || "Unknown"}
                                                </Typography>
                                                {parsedIntelligence?.analysis
                                                  .companyDetails[0].domain && (
                                                  <Typography variant="body2">
                                                    <strong>Domain:</strong>{" "}
                                                    {
                                                      parsedIntelligence
                                                        ?.analysis
                                                        .companyDetails[0]
                                                        .domain
                                                    }
                                                  </Typography>
                                                )}
                                                {parsedIntelligence?.analysis
                                                  .companyDetails[0]
                                                  .companySize && (
                                                  <Typography variant="body2">
                                                    <strong>
                                                      Company size:
                                                    </strong>{" "}
                                                    {
                                                      parsedIntelligence
                                                        ?.analysis
                                                        .companyDetails[0]
                                                        .companySize
                                                    }
                                                  </Typography>
                                                )}
                                                {parsedIntelligence?.analysis
                                                  .companyDetails[0]
                                                  .additionalInfo && (
                                                  <Typography
                                                    variant="body2"
                                                    sx={{
                                                      mt: 1,
                                                      color:
                                                        theme.palette.text
                                                          .secondary,
                                                    }}
                                                  >
                                                    {
                                                      parsedIntelligence
                                                        ?.analysis
                                                        .companyDetails[0]
                                                        .additionalInfo
                                                    }
                                                  </Typography>
                                                )}
                                              </Box>
                                            </Box>
                                          )}

                                          {/* Personal Details Card */}
                                          {parsedIntelligence?.analysis
                                            ?.personalDetails?.[0] && (
                                            <Box
                                              sx={{
                                                mt: 2,
                                                p: 2,
                                                bgcolor:
                                                  theme.palette.background
                                                    .paper,
                                                borderRadius: 1,
                                                boxShadow: 1,
                                              }}
                                            >
                                              <Typography
                                                variant="subtitle2"
                                                sx={{
                                                  fontWeight: "bold",
                                                  display: "flex",
                                                  alignItems: "center",
                                                  gap: 1,
                                                  mb: 1,
                                                }}
                                              >
                                                <PersonIcon fontSize="small" />{" "}
                                                Contact Person
                                              </Typography>
                                              <Box
                                                sx={{
                                                  display: "flex",
                                                  flexDirection: "column",
                                                  gap: 0.5,
                                                }}
                                              >
                                                <Typography variant="body2">
                                                  <strong>Name:</strong>{" "}
                                                  {parsedIntelligence?.analysis
                                                    .personalDetails[0].name ||
                                                    "Unknown"}
                                                </Typography>
                                                {parsedIntelligence?.analysis
                                                  .personalDetails[0]
                                                  .emailAddress && (
                                                  <Typography variant="body2">
                                                    <strong>Email:</strong>{" "}
                                                    {
                                                      parsedIntelligence
                                                        ?.analysis
                                                        .personalDetails[0]
                                                        .emailAddress
                                                    }
                                                  </Typography>
                                                )}
                                                {parsedIntelligence?.analysis
                                                  .personalDetails[0]
                                                  .linkedIn && (
                                                  <Box
                                                    sx={{
                                                      display: "flex",
                                                      alignItems: "center",
                                                      mt: 1,
                                                    }}
                                                  >
                                                    <LinkedInIcon
                                                      sx={{
                                                        mr: 1,
                                                        color: "#0a66c2",
                                                      }}
                                                    />
                                                    <a
                                                      href={
                                                        parsedIntelligence
                                                          ?.analysis
                                                          .personalDetails[0]
                                                          .linkedIn
                                                      }
                                                      target="_blank"
                                                      rel="noopener noreferrer"
                                                      style={{
                                                        textDecoration: "none",
                                                        color: "#0a66c2",
                                                      }}
                                                    >
                                                      LinkedIn Profile
                                                    </a>
                                                  </Box>
                                                )}
                                              </Box>
                                            </Box>
                                          )}

                                          {/* Sentiment Indicator */}
                                          {parsedIntelligence?.analysis
                                            ?.sentimentOverall && (
                                            <Box sx={{ mt: 2 }}>
                                              <Typography
                                                variant="subtitle2"
                                                sx={{
                                                  fontWeight: "bold",
                                                  display: "flex",
                                                  alignItems: "center",
                                                  gap: 1,
                                                }}
                                              >
                                                <MoodIcon fontSize="small" />{" "}
                                                Sentiment
                                              </Typography>
                                              <Box
                                                sx={{
                                                  display: "flex",
                                                  alignItems: "center",
                                                  mt: 1,
                                                  p: 1.5,
                                                  bgcolor:
                                                    parsedIntelligence?.analysis
                                                      .sentimentOverall ===
                                                    "positive"
                                                      ? alpha(
                                                          theme.palette.success
                                                            .main,
                                                          0.1
                                                        )
                                                      : parsedIntelligence
                                                          ?.analysis
                                                          .sentimentOverall ===
                                                        "negative"
                                                      ? alpha(
                                                          theme.palette.error
                                                            .main,
                                                          0.1
                                                        )
                                                      : alpha(
                                                          theme.palette
                                                            .grey[400],
                                                          0.1
                                                        ),
                                                  borderRadius: 1,
                                                }}
                                              >
                                                <Box sx={{ mr: 2 }}>
                                                  {parsedIntelligence?.analysis
                                                    .sentimentOverall ===
                                                  "positive" ? (
                                                    <MoodIcon
                                                      fontSize="large"
                                                      sx={{
                                                        color:
                                                          theme.palette.success
                                                            .main,
                                                      }}
                                                    />
                                                  ) : parsedIntelligence
                                                      ?.analysis
                                                      .sentimentOverall ===
                                                    "negative" ? (
                                                    <MoodIcon
                                                      fontSize="large"
                                                      sx={{
                                                        color:
                                                          theme.palette.error
                                                            .main,
                                                        transform:
                                                          "rotate(180deg)",
                                                      }}
                                                    />
                                                  ) : (
                                                    <MoodIcon
                                                      fontSize="large"
                                                      sx={{
                                                        color:
                                                          theme.palette
                                                            .grey[500],
                                                      }}
                                                    />
                                                  )}
                                                </Box>
                                                <Typography
                                                  variant="body2"
                                                  sx={{
                                                    fontWeight: "medium",
                                                    color:
                                                      parsedIntelligence
                                                        ?.analysis
                                                        .sentimentOverall ===
                                                      "positive"
                                                        ? theme.palette.success
                                                            .main
                                                        : parsedIntelligence
                                                            ?.analysis
                                                            .sentimentOverall ===
                                                          "negative"
                                                        ? theme.palette.error
                                                            .main
                                                        : theme.palette.text
                                                            .primary,
                                                  }}
                                                >
                                                  {parsedIntelligence?.analysis.sentimentOverall
                                                    .charAt(0)
                                                    .toUpperCase() +
                                                    parsedIntelligence?.analysis.sentimentOverall.slice(
                                                      1
                                                    )}
                                                </Typography>
                                              </Box>
                                            </Box>
                                          )}

                                          {/* BANT Analysis */}
                                          {parsedIntelligence?.analysis
                                            ?.bantAnalysis && (
                                            <>
                                              <Typography
                                                variant="subtitle2"
                                                sx={{
                                                  mt: 2,
                                                  fontWeight: "bold",
                                                  display: "flex",
                                                  alignItems: "center",
                                                  gap: 1,
                                                }}
                                              >
                                                <BusinessIcon fontSize="small" />{" "}
                                                BANT Status
                                              </Typography>
                                              <Box
                                                sx={{
                                                  display: "grid",
                                                  gridTemplateColumns:
                                                    "1fr 1fr",
                                                  gap: 2,
                                                  mt: 1,
                                                  bgcolor:
                                                    theme.palette.background
                                                      .paper,
                                                  borderRadius: 1,
                                                  p: 1.5,
                                                }}
                                              >
                                                {/* Budget */}
                                                <Box
                                                  sx={{
                                                    display: "flex",
                                                    flexDirection: "column",
                                                    p: 1,
                                                    borderRadius: 1,
                                                    bgcolor:
                                                      parsedIntelligence
                                                        ?.analysis.bantAnalysis
                                                        .budget === "Yes"
                                                        ? alpha(
                                                            theme.palette
                                                              .success.main,
                                                            0.1
                                                          )
                                                        : alpha(
                                                            theme.palette
                                                              .grey[300],
                                                            0.2
                                                          ),
                                                  }}
                                                >
                                                  <Box
                                                    sx={{
                                                      display: "flex",
                                                      alignItems: "center",
                                                      mb: 0.5,
                                                    }}
                                                  >
                                                    <AttachMoneyIcon
                                                      fontSize="small"
                                                      sx={{ mr: 0.5 }}
                                                    />
                                                    <Typography variant="subtitle2">
                                                      Budget
                                                    </Typography>
                                                  </Box>
                                                  <Typography
                                                    variant="body2"
                                                    sx={{
                                                      fontWeight:
                                                        parsedIntelligence
                                                          ?.analysis
                                                          .bantAnalysis
                                                          .budget === "Yes"
                                                          ? "bold"
                                                          : "normal",
                                                      color:
                                                        parsedIntelligence
                                                          ?.analysis
                                                          .bantAnalysis
                                                          .budget === "Yes"
                                                          ? theme.palette
                                                              .success.main
                                                          : theme.palette.text
                                                              .secondary,
                                                    }}
                                                  >
                                                    {parsedIntelligence
                                                      ?.analysis.bantAnalysis
                                                      .budget || "Unknown"}
                                                  </Typography>
                                                  {parsedIntelligence?.analysis
                                                    .bantAnalysis
                                                    .budgetDetail && (
                                                    <Typography
                                                      variant="caption"
                                                      color="text.secondary"
                                                    >
                                                      {
                                                        parsedIntelligence
                                                          ?.analysis
                                                          .bantAnalysis
                                                          .budgetDetail
                                                      }
                                                    </Typography>
                                                  )}
                                                </Box>

                                                {/* Authority */}
                                                <Box
                                                  sx={{
                                                    display: "flex",
                                                    flexDirection: "column",
                                                    p: 1,
                                                    borderRadius: 1,
                                                    bgcolor:
                                                      parsedIntelligence
                                                        ?.analysis.bantAnalysis
                                                        .authority === "Yes"
                                                        ? alpha(
                                                            theme.palette
                                                              .success.main,
                                                            0.1
                                                          )
                                                        : alpha(
                                                            theme.palette
                                                              .grey[300],
                                                            0.2
                                                          ),
                                                  }}
                                                >
                                                  <Box
                                                    sx={{
                                                      display: "flex",
                                                      alignItems: "center",
                                                      mb: 0.5,
                                                    }}
                                                  >
                                                    <SupervisorAccountIcon
                                                      fontSize="small"
                                                      sx={{ mr: 0.5 }}
                                                    />
                                                    <Typography variant="subtitle2">
                                                      Authority
                                                    </Typography>
                                                  </Box>
                                                  <Typography
                                                    variant="body2"
                                                    sx={{
                                                      fontWeight:
                                                        parsedIntelligence
                                                          ?.analysis
                                                          .bantAnalysis
                                                          .authority === "Yes"
                                                          ? "bold"
                                                          : "normal",
                                                      color:
                                                        parsedIntelligence
                                                          ?.analysis
                                                          .bantAnalysis
                                                          .authority === "Yes"
                                                          ? theme.palette
                                                              .success.main
                                                          : theme.palette.text
                                                              .secondary,
                                                    }}
                                                  >
                                                    {parsedIntelligence
                                                      ?.analysis.bantAnalysis
                                                      .authority || "Unknown"}
                                                  </Typography>
                                                  {parsedIntelligence?.analysis
                                                    .bantAnalysis
                                                    .authorityDetail && (
                                                    <Typography
                                                      variant="caption"
                                                      color="text.secondary"
                                                    >
                                                      {
                                                        parsedIntelligence
                                                          ?.analysis
                                                          .bantAnalysis
                                                          .authorityDetail
                                                      }
                                                    </Typography>
                                                  )}
                                                </Box>

                                                {/* Need */}
                                                <Box
                                                  sx={{
                                                    display: "flex",
                                                    flexDirection: "column",
                                                    p: 1,
                                                    borderRadius: 1,
                                                    bgcolor:
                                                      parsedIntelligence
                                                        ?.analysis.bantAnalysis
                                                        .need === "Yes"
                                                        ? alpha(
                                                            theme.palette
                                                              .success.main,
                                                            0.1
                                                          )
                                                        : alpha(
                                                            theme.palette
                                                              .grey[300],
                                                            0.2
                                                          ),
                                                  }}
                                                >
                                                  <Box
                                                    sx={{
                                                      display: "flex",
                                                      alignItems: "center",
                                                      mb: 0.5,
                                                    }}
                                                  >
                                                    <NeedIcon
                                                      fontSize="small"
                                                      sx={{ mr: 0.5 }}
                                                    />
                                                    <Typography variant="subtitle2">
                                                      Need
                                                    </Typography>
                                                  </Box>
                                                  <Typography
                                                    variant="body2"
                                                    sx={{
                                                      fontWeight:
                                                        parsedIntelligence
                                                          ?.analysis
                                                          .bantAnalysis.need ===
                                                        "Yes"
                                                          ? "bold"
                                                          : "normal",
                                                      color:
                                                        parsedIntelligence
                                                          ?.analysis
                                                          .bantAnalysis.need ===
                                                        "Yes"
                                                          ? theme.palette
                                                              .success.main
                                                          : theme.palette.text
                                                              .secondary,
                                                    }}
                                                  >
                                                    {parsedIntelligence
                                                      ?.analysis.bantAnalysis
                                                      .need || "Unknown"}
                                                  </Typography>
                                                  {parsedIntelligence?.analysis
                                                    .bantAnalysis
                                                    .needDetail && (
                                                    <Typography
                                                      variant="caption"
                                                      color="text.secondary"
                                                    >
                                                      {
                                                        parsedIntelligence
                                                          ?.analysis
                                                          .bantAnalysis
                                                          .needDetail
                                                      }
                                                    </Typography>
                                                  )}
                                                </Box>

                                                {/* Timeline */}
                                                <Box
                                                  sx={{
                                                    display: "flex",
                                                    flexDirection: "column",
                                                    p: 1,
                                                    borderRadius: 1,
                                                    bgcolor:
                                                      parsedIntelligence
                                                        ?.analysis.bantAnalysis
                                                        .timeline === "Yes"
                                                        ? alpha(
                                                            theme.palette
                                                              .success.main,
                                                            0.1
                                                          )
                                                        : alpha(
                                                            theme.palette
                                                              .grey[300],
                                                            0.2
                                                          ),
                                                  }}
                                                >
                                                  <Box
                                                    sx={{
                                                      display: "flex",
                                                      alignItems: "center",
                                                      mb: 0.5,
                                                    }}
                                                  >
                                                    <AccessTimeIcon
                                                      fontSize="small"
                                                      sx={{ mr: 0.5 }}
                                                    />
                                                    <Typography variant="subtitle2">
                                                      Timeline
                                                    </Typography>
                                                  </Box>
                                                  <Typography
                                                    variant="body2"
                                                    sx={{
                                                      fontWeight:
                                                        parsedIntelligence
                                                          ?.analysis
                                                          .bantAnalysis
                                                          .timeline === "Yes"
                                                          ? "bold"
                                                          : "normal",
                                                      color:
                                                        parsedIntelligence
                                                          ?.analysis
                                                          .bantAnalysis
                                                          .timeline === "Yes"
                                                          ? theme.palette
                                                              .success.main
                                                          : theme.palette.text
                                                              .secondary,
                                                    }}
                                                  >
                                                    {parsedIntelligence
                                                      ?.analysis.bantAnalysis
                                                      .timeline || "Unknown"}
                                                  </Typography>
                                                  {parsedIntelligence?.analysis
                                                    .bantAnalysis
                                                    .timelineDetail && (
                                                    <Typography
                                                      variant="caption"
                                                      color="text.secondary"
                                                    >
                                                      {
                                                        parsedIntelligence
                                                          ?.analysis
                                                          .bantAnalysis
                                                          .timelineDetail
                                                      }
                                                    </Typography>
                                                  )}
                                                </Box>
                                              </Box>
                                            </>
                                          )}
                                        </>
                                      )}
                                    </Box>
                                  )}

                                  {/* Memories/Knowledge Graph Tab */}
                                  {tabValue === 1 && (
                                    <Box>
                                      <MemoryAndKnowledgeGraph
                                        selectedCustomer={
                                          conversation?.customer
                                        }
                                        conversation={conversation}
                                      />
                                    </Box>
                                  )}

                                  {/* Validation Results Tab */}
                                  {tabValue === 2 && (
                                    <Box>
                                      <Box sx={{ mb: 2 }}>
                                        <Button
                                          variant="contained"
                                          size="small"
                                          onClick={() =>
                                            validateConversation(conversation)
                                          }
                                          disabled={
                                            isValidating[conversation.id] ||
                                            !conversation.agentId
                                          }
                                          startIcon={
                                            isValidating[conversation.id] ? (
                                              <CircularProgress size={16} />
                                            ) : validationResults[
                                                conversation.id
                                              ] ? (
                                              validationResults[conversation.id]
                                                .passedRules ===
                                              validationResults[conversation.id]
                                                .totalRules ? (
                                                <CheckCircleIcon
                                                  fontSize="small"
                                                  color="inherit"
                                                />
                                              ) : validationResults[
                                                  conversation.id
                                                ].passedRules === 0 ? (
                                                <ErrorIcon
                                                  fontSize="small"
                                                  color="inherit"
                                                />
                                              ) : (
                                                <WarningIcon
                                                  fontSize="small"
                                                  color="inherit"
                                                />
                                              )
                                            ) : null
                                          }
                                          sx={{ mr: 1 }}
                                        >
                                          {isValidating[conversation.id]
                                            ? "Validating..."
                                            : validationResults[conversation.id]
                                            ? "Re-run Validation"
                                            : "Run Validation"}
                                        </Button>

                                        {!conversation.agentId && (
                                          <Typography
                                            variant="body2"
                                            color="text.secondary"
                                            sx={{ mt: 1 }}
                                          >
                                            No agent associated with this
                                            conversation
                                          </Typography>
                                        )}
                                      </Box>
                                      <ValidationResults
                                        results={
                                          validationResults[conversation.id] ||
                                          null
                                        }
                                        loading={
                                          isValidating[conversation.id] || false
                                        }
                                        title="Conversation Validation"
                                        showConversationInfo={false}
                                      />
                                    </Box>
                                  )}
                                </Box>
                              </>
                            );
                          })()}
                        </Box>
                      </AccordionDetails>
                    </Accordion>
                    <Divider component="li" />
                  </React.Fragment>
                ))}
              </List>
            </>
          )}
        </Box>
      ))}

      {hasNextPage && (
        <Box sx={{ textAlign: "center", mt: 2 }}>
          <Button
            onClick={() => fetchNextPage()}
            disabled={isFetchingNextPage}
            variant="contained"
          >
            {isFetchingNextPage ? "Loading more..." : "Load More"}
          </Button>
        </Box>
      )}

      {/* Customer Selection Dialog */}
      <Dialog
        open={customerDialogOpen}
        onClose={closeCustomerDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Choose Customer for Conversation</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1 }}>
            <Autocomplete
              options={customerOptions}
              getOptionLabel={(option) => option.label}
              value={null}
              onChange={(event, newValue) => {
                if (newValue) {
                  assignCustomerToConversation(newValue.id);
                }
              }}
              inputValue={customerSearchText}
              onInputChange={(event, newInputValue) => {
                setCustomerSearchText(newInputValue);
              }}
              renderOption={(props, option) => (
                <Box component="li" {...props}>
                  <Box>
                    <Typography variant="body1">{option.name}</Typography>
                    {option.email !== option.name &&
                      option.email !== "No email" && (
                        <Typography variant="body2" color="text.secondary">
                          {option.email}
                        </Typography>
                      )}
                  </Box>
                </Box>
              )}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Search customers by name or email"
                  placeholder="Type to search..."
                  fullWidth
                />
              )}
              filterOptions={(options, { inputValue }) => {
                const filterValue = inputValue.toLowerCase();
                return options.filter(
                  (option) =>
                    option.name.toLowerCase().includes(filterValue) ||
                    option.email.toLowerCase().includes(filterValue)
                );
              }}
              noOptionsText="No customers found"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeCustomerDialog}>Cancel</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ConversationsList;
